/**
 * ✨ Cost Basis Chain Tracking Utilities
 *
 * Permet de tracer le coût d'acquisition original à travers une chaîne de swaps
 * pour afficher la vraie performance depuis l'investissement initial.
 *
 * Exemple concret :
 * - Achat BTC à 20 000€
 * - Swap BTC → ETH quand BTC vaut 24 000€
 * - ETH baisse à 21 600€
 *
 * Performance "token actuel" : -10% (24 000€ → 21 600€) ❌ Trompeur
 * Performance "origine" : +8% (20 000€ → 21 600€) ✅ Réalité
 *
 * @version 1.4.0
 */

import type { Token, Position } from '../types';

/**
 * Calcule le coût d'acquisition original pour une quantité donnée d'un token
 * en utilisant la méthode FIFO et en traçant les swaps.
 *
 * @param token Token dont on veut calculer le cost basis
 * @param amount Quantité à calculer
 * @returns Coût total d'acquisition original en fiat
 */
export function calculateOriginalCostBasis(
  token: Token | undefined,
  amount: number
): number {
  if (!token?.positions || token.positions.length === 0) {
    return 0;
  }

  let remaining = amount;
  let totalCost = 0;

  // Trier les positions d'achat par date (FIFO)
  const buys = token.positions
    .filter(p => p.type === 'buy')
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  for (const buy of buys) {
    if (remaining <= 0) break;

    const qtyFromThis = Math.min(remaining, buy.quantity);

    // ✨ Utiliser original_cost_basis si disponible (provient d'un swap)
    // Sinon utiliser le prix (achat direct)
    const costBasisPerUnit = buy.original_cost_basis ?? buy.price;
    totalCost += qtyFromThis * costBasisPerUnit;
    remaining -= qtyFromThis;
  }

  return totalCost;
}

/**
 * Calcule le cost basis par unité pour un nouveau token après swap
 *
 * @param originalTotalCost Coût total original en fiat
 * @param newTokenQuantity Quantité du nouveau token reçu
 * @returns Cost basis par unité du nouveau token
 */
export function calculateCostBasisPerUnit(
  originalTotalCost: number,
  newTokenQuantity: number
): number {
  if (newTokenQuantity <= 0) return 0;
  return originalTotalCost / newTokenQuantity;
}

/**
 * Calcule la performance réelle depuis l'investissement initial
 *
 * @param currentValue Valeur actuelle du holding
 * @param originalCostBasis Coût d'acquisition original
 * @returns Pourcentage de gain/perte depuis l'origine
 */
export function calculateOriginPerformance(
  currentValue: number,
  originalCostBasis: number
): number {
  if (originalCostBasis <= 0) return 0;
  return ((currentValue - originalCostBasis) / originalCostBasis) * 100;
}

/**
 * Récupère la chaîne complète des swaps pour une position
 *
 * @param position Position à analyser
 * @param allTokens Tous les tokens du portfolio
 * @returns Array des positions dans l'ordre chronologique de la chaîne
 */
export function getSwapChain(
  position: Position,
  allTokens: Token[]
): Position[] {
  if (!position.swap_chain_id) return [position];

  const chain: Position[] = [];
  const chainId = position.swap_chain_id;

  // Récupérer toutes les positions de cette chaîne
  for (const token of allTokens) {
    const chainPositions = token.positions.filter(
      p => p.swap_chain_id === chainId
    );
    chain.push(...chainPositions);
  }

  // Trier par date
  return chain.sort((a, b) =>
    new Date(a.date).getTime() - new Date(b.date).getTime()
  );
}

/**
 * Génère un ID unique pour une nouvelle chaîne de swap
 *
 * @returns ID unique au format "chain_timestamp_random"
 */
export function generateSwapChainId(): string {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 9);
  return `chain_${timestamp}_${random}`;
}

/**
 * Vérifie si une position fait partie d'une chaîne de swaps
 *
 * @param position Position à vérifier
 * @returns true si la position est liée à des swaps
 */
export function isPartOfSwapChain(position: Position): boolean {
  return !!(position.is_swap || position.swap_chain_id);
}

/**
 * Calcule les métriques de performance duales (token actuel vs origine)
 *
 * @param token Token à analyser
 * @param currentPrice Prix actuel du token
 * @returns Objet contenant les deux performances
 */
export function calculateDualPerformance(
  token: Token,
  currentPrice: number
): {
  currentPerformance: number;
  originPerformance: number | null;
  hasSwapHistory: boolean;
} {
  console.log('🔍 [calculateDualPerformance] START pour token:', token.id);

  if (!token.positions || token.positions.length === 0) {
    console.log('⚠️ [calculateDualPerformance] Aucune position pour', token.id);
    return {
      currentPerformance: 0,
      originPerformance: null,
      hasSwapHistory: false,
    };
  }

  // Calculer la performance "actuelle" (depuis achat du token actuel)
  const buys = token.positions.filter(p => p.type === 'buy');
  console.log('📊 [calculateDualPerformance] Positions d\'achat:', buys.length);

  const totalQuantity = buys.reduce((sum, p) => sum + p.quantity, 0);
  const totalCost = buys.reduce((sum, p) => sum + (p.quantity * p.price), 0);
  const avgCost = totalCost / totalQuantity;
  const currentPerformance = ((currentPrice - avgCost) / avgCost) * 100;

  // Vérifier si c'est un token issu de swap
  console.log('🔎 [calculateDualPerformance] Vérification des champs swap pour chaque position:');
  buys.forEach((p, idx) => {
    console.log(`  Position ${idx}:`, {
      id: p.id,
      is_swap: p.is_swap,
      swap_chain_id: p.swap_chain_id,
      original_cost_basis: p.original_cost_basis,
      isPartOfSwapChain: isPartOfSwapChain(p)
    });
  });

  const hasSwapHistory = buys.some(p => isPartOfSwapChain(p));
  console.log('✨ [calculateDualPerformance] hasSwapHistory:', hasSwapHistory);

  if (!hasSwapHistory) {
    console.log('ℹ️ [calculateDualPerformance] Pas d\'historique de swap, retour performance simple');
    return {
      currentPerformance,
      originPerformance: null,
      hasSwapHistory: false,
    };
  }

  // Calculer la performance "origine" (depuis investissement initial)
  const originalCostBasis = calculateOriginalCostBasis(token, totalQuantity);
  const currentValue = totalQuantity * currentPrice;
  const originPerformance = calculateOriginPerformance(currentValue, originalCostBasis);

  console.log('💰 [calculateDualPerformance] Métriques calculées:', {
    totalQuantity,
    currentPrice,
    currentValue,
    originalCostBasis,
    originPerformance: `${originPerformance.toFixed(2)}%`
  });

  return {
    currentPerformance,
    originPerformance,
    hasSwapHistory: true,
  };
}
